<template>
  <div>
    <div class="box">
      <h2 class="title">{{ $t("global.parkingLot.header.title") }}</h2>

      <div class="activations">
        <div
          class="activation"
          v-for="vehicleActive in vehiclesActivated"
          :key="vehicleActive.id"
        >
          <div v-if="vehicleActive.activation.status == 'ACTIVATED'">
            <Vehicle
              :key="vehicleActive.id"
              :data="vehicleActive"
              buttonType="none"
            ></Vehicle>
            <span class="label"
              >Válido até
              <span style="color: #fff">{{
                vehicleActive.activation.endDate | moment("HH:mm")
              }}</span></span
            >
            <div class="progress-bar">
              <div
                class="bar"
                :style="{
                  width: `${100 * vehicleActive.activation.remainingPercent}%`,
                }"
              ></div>
            </div>
            <hr />
          </div>
        </div>
      </div>

      <p class="has-text-centered">
        <button class="button is-rounded is-primary" @click="openParking()">
          {{ $t("global.parkingLot.button.title") }}
        </button>
      </p>
    </div>

    <zul-modal
      ref="parkingModal"
      :title="modalTitle"
      primary=""
      @primary="startParking"
      @secondary="back"
    >
      <div v-if="loading">
        <p class="loading">{{ $t("global.loading") }}</p>
      </div>
      <div v-else-if="step === 1">
        <div v-if="vehicles.length > 0">
          <Vehicle
            v-for="vehicle in vehicles"
            :key="vehicle.id"
            :data="vehicle"
            buttonType="arrow"
            @click="carSelected"
          ></Vehicle>
        </div>
        <div class="vehicles-empty" v-else>
          <p>
            {{ $t("publicParking.noResults.title") }}
          </p>
        </div>
      </div>

      <div v-else-if="step === 2">
        <Vehicle :data="vehicle" buttonType="none"></Vehicle>
        <fieldset class="form is-green">
          <div class="change-city control has-icons-left">
            <div class="select">
              <select
                :class="{ filled: activation.zoneType }"
                v-model="activation.zoneType"
                id="cad-rule"
                name="cad-rule"
                v-validate="'required'"
                @change="reloadCadOptions"
              >
                <option
                  v-for="zone in zoneTypes"
                  :key="zone.id"
                  :value="zone"
                  :selected="zone.selected"
                >
                  {{ zone.name
                  }}{{ zone.operationHour ? ` - ${zone.operationHour}` : "" }}
                </option>
              </select>
              <label
                for="cad-rule"
                :class="{ 'is-danger': errors.has('cad-rule') }"
                >{{
                  $t("publicParking.activation.zoneType.sheet.title")
                }}</label
              >
            </div>
            <div class="icon is-small is-left is-pin"></div>
          </div>

          <div
            v-if="cadOptions.length > 0"
            class="change-city control has-icons-left"
          >
            <div class="select">
              <select
                :class="{ filled: activation.cads }"
                v-model="activation.cads"
                id="cad-quantity"
                name="cad-quantity"
                v-validate="'required'"
              >
                <option
                  v-for="cad in cadOptions"
                  :key="cad.key"
                  :value="cad.value"
                >
                  {{ cad.name }}
                </option>
              </select>
              <label
                for="cad-quantity"
                :class="{ 'is-danger': errors.has('cad-quantity') }"
                >{{
                  !app.typeCard
                    ? $t(
                        "publicParking.activation.zoneTypeOptionTemplate.cads.title"
                      )
                    : $t(
                        "publicParking.activation.zoneTypeOptionTemplate.cads.title2"
                      )
                }}</label
              >
            </div>
            <div class="icon is-small is-left is-cad"></div>
          </div>
        </fieldset>
      </div>
      <div v-else-if="step === 3">
        <p class="info">{{ $t("publicParking.activation.payment.title") }}</p>
        <br />
        <zul-button
          class="is-large"
          v-for="p in paymentTypes"
          :key="p.type"
          :data="{}"
          @click="setPaymentType(p.type)"
          :disabled="!p.enabled"
        >
          <span
            slot="icon"
            :class="{
              icon: true,
              'is-card': p.type == 'CREDIT' || p.type == 'DEBIT',
              'is-bankslip': p.type == 'BANKSLIP',
            }"
          >
            <svg
              v-if="p.type === 'PIX'"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.7317 13.44C11.9423 13.2295 12.3094 13.2288 12.5199 13.44L15.5198 16.4397C16.0746 16.9944 16.8126 17.2999 17.5973 17.2999H17.9587L14.1484 21.1101C12.9619 22.2966 11.0381 22.2966 9.85151 21.1101L6.05229 17.3108H6.64342C7.42813 17.3108 8.1661 17.0053 8.721 16.4507L11.7317 13.44ZM5.20156 7.53994C5.25 7.55819 5.30137 7.57118 5.35621 7.57118H6.64344C7.1847 7.57118 7.7144 7.79064 8.09688 8.17331L11.1079 11.1843C11.3886 11.4648 11.7572 11.6053 12.126 11.6053C12.4946 11.6053 12.8634 11.4648 13.144 11.1841L16.1438 8.18422C16.5262 7.80155 17.056 7.58209 17.5973 7.58209H18.6441C18.702 7.58209 18.7569 7.56939 18.8076 7.54907L21.1101 9.85158C22.2966 11.0381 22.2966 12.9619 21.1101 14.1485L18.8077 16.4509C18.7569 16.4305 18.7021 16.4179 18.6441 16.4179H17.5973C17.056 16.4179 16.5262 16.1985 16.1438 15.8157L13.144 12.8162C12.6004 12.272 11.6518 12.2722 11.1076 12.816L8.09688 15.8267C7.7144 16.2094 7.1847 16.4288 6.64344 16.4288H5.35621C5.30137 16.4288 5.24991 16.4418 5.20147 16.4601L2.88993 14.1485C1.70336 12.9619 1.70336 11.0381 2.88993 9.85158L5.20156 7.53994ZM9.85153 2.88993C11.0381 1.70336 12.9619 1.70336 14.1485 2.88993L17.9586 6.70008H17.5973C16.8126 6.70008 16.0746 7.00562 15.5197 7.56024L12.52 10.5603C12.3026 10.7783 11.9485 10.7774 11.7317 10.5606L8.72093 7.54933C8.16611 6.99471 7.42815 6.68908 6.64344 6.68908H6.05241L9.85153 2.88993Z"
                fill="currentColor"
              />
            </svg>
          </span>

          {{ p.name | lowercase | capitalize }}
        </zul-button>

        <p class="warning">{{ message }}</p>
      </div>
      <div v-else-if="step === 4">
        <template v-if="activation.paymentType === 'CREDIT'">
          <zul-button
            class="is-large"
            :data="{}"
            v-for="p in paymentMethods"
            :key="p.id"
            @click="setPaymentMethod(p)"
          >
            **** **** **** {{ p.lastDigits }}
            <span slot="sub"
              ><img :src="p.processor.image['@2x']" width="60"
            /></span>
          </zul-button>

          <a class="button is-text is-medium" @click="addUserCard('credit')">{{
            $t("publicParking.activation.payment.newCreditCardButton.title")
          }}</a>
        </template>
        <template v-else>
          <zul-button
            class="is-large"
            :data="{}"
            v-for="p in paymentDebitMethods"
            :key="p.id"
            @click="setPaymentMethod(p)"
          >
            **** **** **** {{ p.lastDigits }}
            <span slot="sub"
              ><img :src="p.processor.image['@2x']" width="60"
            /></span>
          </zul-button>

          <a class="button is-text is-medium" @click="addUserCard('debit')">{{
            $t("publicParking.activation.payment.newDebitCardButton.title")
          }}</a>
        </template>
      </div>
      <div v-else>
        <p>{{ confirmationText }}</p>
        <br />
        <div v-if="activation.paymentType">
          <div
            v-if="
              activation.paymentType == 'CREDIT' ||
              activation.paymentType == 'DEBIT'
            "
          >
            <zul-button class="is-view is-large is-secondary">
              **** **** **** {{ activation.paymentMethod.lastDigits }}
              <span slot="sub"
                ><img
                  :src="activation.paymentMethod.processor.image['@2x']"
                  width="60"
              /></span>
            </zul-button>
          </div>
          <div v-else-if="activation.paymentType == 'BALANCE'">
            <zul-button class="is-view is-large is-secondary">
              {{ activation.paymentMethod.detail }}
              <span slot="sub"
                ><img
                  :src="activation.paymentMethod.processor.image['@2x']"
                  width="60"
              /></span>
            </zul-button>
          </div>
          <a class="button is-text is-medium" @click="changePaymentMethod()">{{
            $t(
              "publicParking.activation.payment.changePaymentMethodButton.title"
            )
          }}</a>
        </div>
      </div>

      <div class="error" v-if="error">
        <p class="has-text-danger">
          {{ $t("publicParking.activation.payment.error.message") }}
        </p>
      </div>
    </zul-modal>
  </div>
</template>

<script>
import ZulButton from "../../components/Button.vue";
import Vehicle from "../../components/Vehicle.vue";
import moment from "moment";
import "clientjs";

export default {
  data() {
    const vehicles = this.$auth.user().vehicles;

    return {
      step: 1,
      vehicle: { type: "CAR" },
      activation: {},
      activations: [],
      vehicles: vehicles || [],
      types: null,
      loading: false,
      error: null,
      position: null,
      zoneTypes: null,
      cads: null,
      cadOptions: [],
      confirmationText: "",
      modalTitle: this.$t("publicParking.header.title"),
      paymentTypes: [],
      paymentMethods: [],
      paymentDebitMethods: [],
      paymentType: null,
      message: null,
      polling: null,
    };
  },
  created() {
    this.paymentMethods = this.$auth
      .user()
      .paymentMethods.filter((p) => p.type === "CREDIT");
    this.paymentDebitMethods = this.$auth
      .user()
      .paymentMethods.filter((p) => p.type === "DEBIT");

    const pollingIntervalSeconds = 5;
    this.polling = setInterval(() => {
      this.vehicles = [
        ...this.vehicles.map((v) => {
          if (v.activation?.status === "ACTIVATED") {
            v.activation.currentDate = moment(v.activation.currentDate).add(
              pollingIntervalSeconds,
              "seconds"
            );
          }
          return v;
        }),
      ];
    }, pollingIntervalSeconds * 1000);
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  computed: {
    vehiclesActivated() {
      return this.vehicles
        .filter((v) => v.activation)
        .map((v) => {
          const diff =
            moment(v.activation.endDate) - moment(v.activation.currentDate);
          v.activation.remainingPercent =
            diff / (v.activation.totalTime * 1000);
          return v;
        });
    },
  },
  methods: {
    setPaymentMethod(card) {
      this.activation.card = card;
      this.activation.paymentType = card.type;
      this.activation.paymentMethod = card;
      this.step = 6;

      this.$http
        .get(`/user/payment/${card.id}?productId=${this.activation.product.id}`)
        .then((res) => {
          this.confirmationText = res.data.message.body;
        });
    },
    changePaymentMethod() {
      this.activation = { ...this.activation, paymentMethod: null };
      this.error = "";
      this.step = 3;
    },
    addUserCard(type) {
      this.$root.$emit("add-user-card", type);
      this.$root.$on("add-user-card-end", () => {
        this.paymentMethods = this.$auth
          .user()
          .paymentMethods.filter((p) => p.type === "CREDIT");
        this.paymentDebitMethods = this.$auth
          .user()
          .paymentMethods.filter((p) => p.type === "DEBIT");
      });
    },
    setPaymentType(type) {
      this.activation.paymentType = type;
      type = type.toLowerCase();

      // reset message
      this.message = null;

      if (type === "credit" || type === "debit") {
        this.step++;
        if (
          (type === "credit" && this.paymentMethods.length === 0) ||
          (type === "debit" && this.paymentDebitMethods.length === 0)
        ) {
          this.addUserCard(type);
        }
      } else if (type === "bankslip") {
        this.step = 5;
      } else if (type === "pix") {
        this.step = 5;
        this.$refs.parkingModal.setProps({
          labels: {
            primary: this.$t(
              "publicParking.activation.payment.pix.generateButton.title"
            ),
          },
        });
      }
    },
    reloadCadOptions() {
      if (!this.app.typeCard) {
        this.cadOptions = this.cads
          .map((option, index) => {
            const price = this.activation.zoneType.priceTable
              ? this.activation.zoneType.priceTable[option.quantity]
              : this.activation.zoneType.price * option.quantity;
            if (!price && price != 0) return false;
            if (!option.zoneTypeMap[this.activation.zoneType.id]) return false;

            const formatedPrice = new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(price);
            return {
              value: option,
              key: index,
              name: `${formatedPrice} - ${this.formatTime(
                this.activation.zoneType.factor * option.quantity
              )}`,
            };
          })
          .filter((item) => item != false);
      }
    },
    formatTime(factor) {
      const totalDuration = parseInt(factor * 60);
      const hours = Math.floor(totalDuration / 60);
      const minutes = totalDuration % 60;
      let totalTime = "";
      if (hours > 0) {
        totalTime += String(hours);
        if (minutes == 0) {
          totalTime +=
            " " +
            (hours > 1
              ? this.$t("time.hour.plural")
              : this.$t("time.hour.singular"));
        } else {
          totalTime += this.$t("time.hour.abbreviated") + minutes;
        }
      } else {
        totalTime += String(minutes) + " " + this.$t("time.minute.abbreviated");
      }

      return totalTime;
    },
    getLocation() {
      return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve(position),
            (err) => reject(err)
          );
        }
      });
    },
    getFingerprint() {
      // eslint-disable-next-line no-undef
      const client = new ClientJS(); // dependencia carregada via import
      const fingerprint = client.getFingerprint();

      return fingerprint;
    },
    resetModal() {
      this.step = 1;
      this.activation = {};
      this.cadOptions = [];
      this.error = null;
    },
    resetAll() {
      this.resetModal();
    },
    async openParking() {
      this.resetAll();
      this.loading = true;
      this.$refs.parkingModal.open();

      try {
        this.position = await this.getLocation();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Error getting geolocation", err);
      }

      this.$refs.parkingModal.setProps({ labels: { primary: "" } });
      this.loading = false;
    },
    async carSelected(data) {
      this.loading = true;
      this.vehicle = data;
      const fingerprint = this.getFingerprint();

      try {
        const prepareResponse = await this.prepare(
          this.vehicle.id,
          fingerprint,
          this.position?.coords?.latitude,
          this.position?.coords?.longitude,
          this.position?.coords?.accuracy
        );

        this.zoneTypes = prepareResponse.zoneTypes;
        this.cads = prepareResponse.options;

        if (this.app.typeCard) {
          this.cadOptions = this.cads.map((option, index) => ({
            value: option,
            key: index,
            name:
              option.quantity > 1
                ? this.$t(
                    "publicParking.activation.zoneTypeOptionTemplate.zoneType.option.cad.title.plural",
                    [option.quantity]
                  )
                : this.$t(
                    "publicParking.activation.zoneTypeOptionTemplate.zoneType.option.cad.title.singular",
                    [option.quantity]
                  ),
          }));
        }

        this.step += 1;

        this.$refs.parkingModal.setProps({
          labels: { primary: this.$t("publicParking.parkButton.title") },
        });
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          this.$t("global.error.message");
        this.$buefy.toast.open({
          message,
          type: "is-danger",
        });
      }
      this.loading = false;
    },
    startParking() {
      const fingerprint = this.getFingerprint();

      switch (this.step) {
        case 6:
          this.loading = true;

          this.parkingActivation(
            this.vehicle.id,
            fingerprint,
            this.activation.zoneType.id,
            this.activation.cads.quantity,
            this.position?.coords?.latitude,
            this.position?.coords?.longitude,
            this.position?.coords?.accuracy,
            this.activation.paymentType,
            this.activation.paymentMethod,
            this.activation.product
          )
            .then((activationResponse) => {
              if (activationResponse.status === "ACTIVATED") {
                this.$refs.parkingModal.close();

                for (let i = 0; i < this.vehicles.length; i++) {
                  const vehicle = this.vehicles[i];
                  if (vehicle.id === this.vehicle.id) {
                    this.vehicles[i].activation = activationResponse;
                  }
                }

                this.vehicles = [...this.vehicles];
                this.$root.$emit("update-user-funds");
                this.$root.$emit("reload-historic");
              } else {
                this.error = this.message;
              }
              this.loading = false;
            })
            .catch((err) => {
              this.error = err;
              this.loading = false;
            });
          break;

        default:
          this.$validator
            .validate()
            .then((valid) => {
              if (!valid) {
                throw new Error(this.$t("global.validation.message"));
              }
              return valid;
            })
            .then(() => {
              const zoneType = this.activation?.cads?.zoneTypeMap
                ? this.activation?.cads?.zoneTypeMap[
                    this.activation.zoneType.id
                  ]
                : this.activation?.cads;
              if (zoneType) {
                this.confirmationText = this.activation?.cads?.zoneTypeMap
                  ? this.activation.cads.zoneTypeMap[
                      this.activation.zoneType.id
                    ].message.body
                  : zoneType.message.body;

                if (zoneType.hasFunds) {
                  this.step = 5;
                } else {
                  if (zoneType?.product?.id) {
                    this.$http
                      .get(
                        `/order/paymenttypes?action=ACTIVATION&productId=${zoneType.product.id}`
                      )
                      .then((res) => {
                        this.isLoading = false;
                        this.paymentTypes = res.data.items.filter(
                          (o) =>
                            //o.type === "BANKSLIP" ||
                            //o.type === "PIX" ||
                            o.type === "DEBIT" ||
                            (o.type === "CREDIT" && !o.wallet)
                        );
                        this.message = res.data.message.body;
                      });

                    this.activation.product = zoneType.product;

                    if (zoneType.preferredPaymentMethod) {
                      this.activation.paymentMethod =
                        zoneType.preferredPaymentMethod;
                      this.activation.paymentType =
                        zoneType.preferredPaymentMethod.type;

                      this.step = 5;

                      this.activation = { ...this.activation };
                    } else {
                      this.step += 1;
                    }
                  }
                }

                this.$refs.parkingModal.setProps({
                  labels: { primary: this.$t("global.confirm") },
                });
              } else {
                this.$refs.parkingModal.setProps({ labels: { primary: "" } });
                this.confirmationText = this.$t(
                  "publicParking.noFunds.message"
                );
              }
              this.step += 1;
            })
            .catch((err) => {
              const message = err?.message || this.$t("global.error.message");

              this.$buefy.toast.open({
                message,
                type: "is-danger",
              });
            });

          break;
      }
    },
    back() {
      this.resetModal();
      this.$refs.parkingModal.close();
    },
    async parkingActivation(
      vehicleId,
      udid,
      zoneTypeId,
      quantity,
      lat,
      lng,
      accuracy,
      paymentType,
      paymentMethod,
      product
    ) {
      const data = new FormData();
      data.append("vehicleId", vehicleId);
      data.append("udid", udid);
      data.append("zoneTypeId", zoneTypeId);
      data.append("quantity", quantity);

      if (lat && lng && accuracy) {
        data.append("lat", lat);
        data.append("lng", lng);
        data.append("accuracy", accuracy);
      }

      if (
        paymentType === "CREDIT" ||
        paymentType === "DEBIT" ||
        paymentType === "BALANCE"
      ) {
        data.append("paymentMethodId", paymentMethod.id);
        data.append("paymentSource", paymentMethod.gateway);
        data.append("productId", product.id);
      } else if (paymentType === "BANKSLIP") {
        data.append("productId", product.id);
        data.append("email", this.$auth.user().email);
      } else if (paymentType === "pix") {
        const redeemCode =
          (
            (
              this.paymentTypes.find(
                (payment) => payment.type.toLowerCase() === "pix"
              ) || {}
            ).redeem || {}
          ).code || null;

        data.append("productId", product.id);

        if (redeemCode) {
          data.append("redeemCode", redeemCode);
        }
      }

      this.error = null;

      const response = await this.$http.post("/cad/activation", data);

      return response.data;
    },
    async prepare(vehicleId, udid, lat, lng, accuracy) {
      const data = new FormData();
      data.append("vehicleId", vehicleId);
      data.append("udid", udid);

      if (lat && lng && accuracy) {
        data.append("lat", lat);
        data.append("lng", lng);
        data.append("accuracy", accuracy);
      }

      this.error = null;

      const response = await this.$http.post("/cad/activation/prepare", data);

      return response.data;
    },
  },
  components: {
    Vehicle,
    ZulButton,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../../assets/sass/variables";

hr {
  height: 1px;
  margin: 0.8rem 0;
}
.select select {
  text-transform: none;
}
.label {
  color: #666;
  font-size: 13px;
}

.dashboard .form {
  padding-top: 1.2rem;
}

.card-brand {
  width: 30px;
  margin-right: 10px;
  margin-bottom: -4px;
}

.progress-bar {
  background: #666;
  height: 10px;
  position: relative;
  overflow: hidden;
  width: 100%;

  > .bar {
    // background: $primary;
    // multicity colors
    @each $city, $map in $cities {
      .is-#{$city} & {
        background-color: map-get($map, "primary");
      }
    }
    display: block;
    height: 100%;
    left: 0;
    padding: 3.33px;
    position: absolute;
    top: 0;
  }
}
</style>
